import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useVerifyResetTokenQuery, useResetPasswordMutation } from '../store/api/userApiSlice';
import Navbar from '../components/Navbar/Navbar';
import Footer from '../components/Footer/Footer';
import PasswordInput from '../components/Auth/PasswordInput';
import '../components/Auth/Auth.css';
import './ResetPassword.css';

function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const token = searchParams.get('token');
  const id = searchParams.get('id');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(null);
  
  // Verify token on mount
  const { data: tokenData, isLoading: isVerifying, error: tokenError } = useVerifyResetTokenQuery(
    { token, id },
    { skip: !token || !id }
  );
  
  // Reset password mutation
  const [resetPassword, { isLoading: isResetting, error: resetError, isSuccess }] = useResetPasswordMutation();

  // Handle reset success
  useEffect(() => {
    if (isSuccess) {
      setSuccess('Password reset successfully! Redirecting to login...');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    }
  }, [isSuccess, navigate]);

  // Handle API errors
  useEffect(() => {
    if (resetError) {
      setError(resetError?.data?.message || 'Failed to reset password. Please try again.');
    }
  }, [resetError]);

  const checkPasswordStrength = (pwd) => {
    const hasUpperCase = /[A-Z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSpecialChar = /[!@#$%^&*]/.test(pwd);
    const isLongEnough = pwd.length >= 8;

    if (hasUpperCase && hasNumber && hasSpecialChar && isLongEnough) {
      setPasswordStrength('strong');
    } else {
      setPasswordStrength('weak');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!password) {
      setError('Password is required');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      await resetPassword({ 
        token, 
        id, 
        password 
      }).unwrap();
    } catch (err) {
      console.error('Reset password failed:', err);
    }
  };

  // Show loading while verifying token
  if (isVerifying) {
    return (
      <div className="reset-password-page">
        <Navbar />
        <div className="reset-password-container">
          <div className="reset-password-card">
            <h1 className="form-title">Verifying...</h1>
            <p>Please wait while we verify your reset link.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Show error if token is invalid
  if (tokenError || (!token || !id)) {
    return (
      <div className="reset-password-page">
        <Navbar />
        <div className="reset-password-container">
          <div className="reset-password-card">
            <h1 className="form-title">Invalid Link</h1>
            <div className="error-banner">
              The password reset link is invalid or has expired.
            </div>
            <p className="reset-description">
              Please request a new password reset link.
            </p>
            <button 
              className="submit-btn" 
              onClick={() => navigate('/')}
            >
              GO TO HOME
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <Navbar />
      <div className="reset-password-container">
        <div className="reset-password-card">
          <h1 className="form-title">Reset Password</h1>

          {error && (
            <div className="error-banner">
              {error}
              <button 
                type="button" 
                className="error-close"
                onClick={() => setError('')}
              >
                ×
              </button>
            </div>
          )}

          {success && (
            <div className="success-banner">
              {success}
            </div>
          )}

          {!success && (
            <form onSubmit={handleSubmit}>
              <p className="reset-description">
                Enter your new password below.
              </p>

              <PasswordInput
                label="New Password"
                name="password"
                placeholder="Enter new password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  checkPasswordStrength(e.target.value);
                  setError('');
                }}
                showStrength
                strength={passwordStrength}
              />
              <p className="password-note">
                Password must contain uppercase, numbers and special characters.
              </p>

              <PasswordInput
                label="Confirm Password"
                name="confirmPassword"
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setError('');
                }}
              />

              <button 
                type="submit" 
                className="submit-btn" 
                disabled={isResetting}
              >
                {isResetting ? 'RESETTING...' : 'RESET PASSWORD'}
              </button>
            </form>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default ResetPassword;
